import React, { useContext, useEffect, useState } from 'react';
import { DrawerComponent } from 'components/Drawer';
import { FeatureContext } from 'contexts/FeaturesContext';
import { api } from 'services/api';

type KoinTier = {
  id: number;
  name: string;
  koin_quantity: number;
  discount_percentage: number;
  total_price_brl: number;
  unit_price_brl: number;
  installment_discount_percentage?: number;
  installment_count?: number;
  installment_price_brl?: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  idToken: string | null;
};

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);

// Dados de parcelamento fixos por koin_quantity (backend ainda não retorna)
const INSTALLMENT_FALLBACK: Record<number, {
  installment_discount_percentage: number;
  installment_count: number;
  installment_price_brl: number;
}> = {
  350000: { installment_discount_percentage: 28, installment_count: 3, installment_price_brl: 2176 },
  750000: { installment_discount_percentage: 34, installment_count: 4, installment_price_brl: 3232 },
  1000000: { installment_discount_percentage: 40, installment_count: 5, installment_price_brl: 3132 },
  3000000: { installment_discount_percentage: 44, installment_count: 6, installment_price_brl: 7280 },
};

const PromoBadge = () => (
  <span
    style={{
      background:
        'linear-gradient(to right, #B1D6F6, #56A6EB, #3B2358, #B77CCB, #DFC5E8)',
      color: '#fff',
      fontSize: '11px',
      fontWeight: 600,
      borderRadius: '12px',
      padding: '4px 8px',
      whiteSpace: 'nowrap',
    }}
  >
    Promoção de lançamento
  </span>
);

export const ComprarKoinsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  accessToken,
  idToken,
}) => {
  const { dataAgency } = useContext(FeatureContext);
  const [tiers, setTiers] = useState<KoinTier[]>([]);
  const [basePriceBrl, setBasePriceBrl] = useState<number>(0);
  const [selectedTier, setSelectedTier] = useState<KoinTier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isParcelarOpen, setIsParcelarOpen] = useState(false);

  const userName = localStorage.getItem('name') ?? '';
  const companyName = dataAgency?.name ?? '';

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    const fetchTiers = async () => {
      try {
        const { data } = await api.get('lya/koin-tiers', {
          headers: { Authorization: `Bearer ${accessToken}`, idToken },
        });
        const mergedTiers: KoinTier[] = data.data.tiers.map((tier: KoinTier) =>
          tier.installment_count
            ? tier
            : { ...tier, ...(INSTALLMENT_FALLBACK[tier.koin_quantity] ?? {}) },
        );
        setTiers(mergedTiers);
        setBasePriceBrl(data.data.base_price_brl);
        if (mergedTiers.length > 0) setSelectedTier(mergedTiers[0]);
      } catch (e) {
        setError('Não foi possível carregar os pacotes de koins.');
      }
    };
    fetchTiers();
  }, [isOpen, accessToken, idToken]);

  const isFirstTier = selectedTier?.id === tiers[0]?.id;

  const handleBuyAVista = async () => {
    if (!selectedTier) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(
        'lya/buy',
        {
          id_tier: selectedTier.id,
          koin_quantity: selectedTier.koin_quantity,
          amount_brl: selectedTier.total_price_brl,
          success_url: `${window.location.origin}/lya/koin/success`,
          cancel_url: window.location.href,
        },
        { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
      );
      if (data.data?.checkout_url) {
        const tab = window.open(data.data.checkout_url, '_blank');
        if (tab) {
          const poll = setInterval(() => {
            try {
              if (tab.closed) {
                clearInterval(poll);
                return;
              }
              if (tab.location.href) {
                tab.close();
                clearInterval(poll);
              }
            } catch {
              /* cross-origin: still on Stripe */
            }
          }, 500);
        }
        onClose();
      }
    } catch (e) {
      setError('Erro ao iniciar compra. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    if (!selectedTier) return;
    const koinQty = selectedTier.koin_quantity.toLocaleString('pt-BR');
    const message = `Olá! Meu nome é ${userName} da ${companyName}. Quero parcelar o pacote de ${koinQty} koins da Lya.`;
    window.open(
      `https://wa.me/5511992359399?text=${encodeURIComponent(message)}`,
      '_blank',
    );
  };

  const parcelarTotal =
    selectedTier?.installment_count && selectedTier?.installment_price_brl
      ? selectedTier.installment_count * selectedTier.installment_price_brl
      : null;

  return (
    <>
      <DrawerComponent
        open={isOpen}
        size='default'
        titleClose='Comprar koins'
        extra={<PromoBadge />}
        onCompleteClose={onClose}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            paddingBottom: '24px',
          }}
        >
          <p
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              margin: '8px 0 14px',
            }}
          >
            Selecione o pacote:
          </p>

          {error && (
            <p
              style={{
                color: '#c0392b',
                fontSize: '13px',
                marginBottom: '12px',
              }}
            >
              {error}
            </p>
          )}

          {/* Tier list */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            {tiers.map(tier => {
              const isSelected = selectedTier?.id === tier.id;
              const originalPrice = basePriceBrl * tier.koin_quantity;


              return (
                <div
                  key={tier.id}
                  onClick={() => setSelectedTier(tier)}
                  style={{
                    border: `2px solid ${isSelected ? '#E0234E' : '#e0e0e0'}`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    background: '#fff',
                  }}
                >
                  {/* Radio indicator */}
                  <div style={{
                    height: '100%',
                    flex: '1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: isSelected
                          ? `5px solid #E0234E`
                          : `2px solid #ccc`,
                        flexShrink: 0,

                      }}
                    />

                    {/* Tier name + installment hint */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: '15px',
                          color: '#1a1a1a',
                        }}
                      >
                        {tier.koin_quantity.toLocaleString('pt-BR')} Koins
                      </div>

                    </div>
                  </div>
                  {/* Price block */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div
                      style={{
                        color: '#999',
                        fontSize: '12px',
                        textDecoration: 'line-through',
                        marginBottom: '2px',
                      }}
                    >
                      {formatBRL(originalPrice)}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {tier.discount_percentage > 0 && (
                        <span
                          style={{
                            color: '#E0234E',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          -{tier.discount_percentage}% à vista
                        </span>
                      )}
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: '15px',
                          color: '#1a1a1a',
                        }}
                      >
                        {formatBRL(tier.total_price_brl)}
                      </span>
                    </div>
                    {tier.installment_count && <div>
                      <span
                        style={{
                          color: '#999',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        ou {tier.installment_discount_percentage} parcelado em {tier.installment_count}x {formatBRL(tier.installment_price_brl as number)}
                      </span>
                    </div>}

                  </div>
                </div>
              );
            })}

            {/* Description inside scroll area */}
            <p
              style={{
                color: '#555',
                fontSize: '13px',
                lineHeight: 1.5,
                margin: '16px 0 4px',
                flexShrink: 0,
              }}
            >
              Os pacotes de koins são utilizados quando o saldo do plano acaba e
              são válidos por um ano a partir da data da compra.
            </p>
          </div>

          {/* Footer — only buttons, always visible */}
          <div style={{ flexShrink: 0, paddingTop: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'end' }}>
              {!isFirstTier && (
                <button
                  onClick={() => setIsParcelarOpen(true)}
                  disabled={!selectedTier}
                  style={{

                    padding: '12px 24px',
                    background: '#fff',
                    color: '#E0234E',
                    border: '1px solid #E0234E',
                    borderRadius: '8px',
                    cursor: !selectedTier ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                >
                  Comprar parcelado
                </button>
              )}
              <button
                onClick={handleBuyAVista}
                disabled={loading || !selectedTier}
                style={{
                  padding: '12px 24px',
                  background: loading || !selectedTier ? '#ccc' : '#E0234E',
                  color: '#fff',
                  border: '1px solid #E0234E',
                  borderRadius: '8px',
                  cursor: loading || !selectedTier ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                {loading ? 'Aguarde...' : 'Comprar à vista'}
              </button>
            </div>
          </div>
        </div>
      </DrawerComponent>

      {/* Parcelar pacote modal */}
      {isParcelarOpen && selectedTier && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '28px 24px 24px',
              width: '90%',
              maxWidth: '600px',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setIsParcelarOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '22px',
                color: '#555',
                lineHeight: 1,
              }}
            >
              ×
            </button>

            <h3
              style={{
                margin: '0 0 20px',
                fontSize: '18px',
                fontWeight: 700,
                color: '#1a1a1a',
              }}
            >
              Parcelar o pacote
            </h3>

            {/* Tier summary card */}
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',

                }}
              >
                <span style={{ fontSize: '16px', color: '#808080' }}>
                  Pacote selecionado
                </span>
                {selectedTier.installment_count &&
                  selectedTier.installment_price_brl && (
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#808080' }}>
                      {selectedTier.installment_count}x de R${' '}
                      {selectedTier.installment_price_brl.toLocaleString(
                        'pt-BR',
                      )}
                    </span>
                  )}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: '24px' }}>
                  {selectedTier.koin_quantity.toLocaleString('pt-BR')}<span style={{ fontWeight: 500, fontSize: '16px' }}> Koins</span> 
                </span>
                {parcelarTotal !== null && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '6px',
                    }}
                  >
                    <span
                      style={{
                        color: '#E0234E',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      -{selectedTier.installment_discount_percentage}%
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '24px' }}>
                      {formatBRL(parcelarTotal)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <p
              style={{
                fontSize: '14px',
                color: '#555',
                lineHeight: 1.6,
                marginBottom: '24px',
              }}
            >
              Entre em contato com um de nossos consultores pelo WhatsApp. Eles
              vão te enviar um link de pagamento com as condições do pacote
              selecionado.
            </p>

            <div style={{
              display: 'flex',
              justifyContent: 'end'
            }}>
              <button
                onClick={handleWhatsApp}
                style={{

                  padding: '12px 24px',
                  background: '#E0234E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '16px',
                }}
              >
                Conversar com consultor
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
